import {useCallback, useEffect, useState} from 'react';

import {safeLocalStorage} from '@/utils/storage';

export const GROUP_ORDERING_CALLOUT_VIEWS = 'groupOrderingCalloutViews';

export const GROUP_ORDERING_CALLOUT_DISMISSED = 'groupOrderingCalloutDismissed';

export const GROUP_ORDERING_CALLOUT_MAX_VIEWS = 2;

const readViews = () => {
  const stored = Number(safeLocalStorage.getItem(GROUP_ORDERING_CALLOUT_VIEWS));

  return Number.isInteger(stored) && stored > 0 ? stored : 0;
};

const isDismissed = () => safeLocalStorage.getItem(GROUP_ORDERING_CALLOUT_DISMISSED) === 'true';

// A view is one page load, not one mount:
// several elements can advertise group ordering on the same page
let viewsAtPageLoad: number | null = null;

const recordPageLoadView = () => {
  if (viewsAtPageLoad === null) {
    viewsAtPageLoad = readViews();

    if (viewsAtPageLoad < GROUP_ORDERING_CALLOUT_MAX_VIEWS) {
      safeLocalStorage.setItem(GROUP_ORDERING_CALLOUT_VIEWS, String(viewsAtPageLoad + 1));
    }
  }

  return viewsAtPageLoad;
};

// Every mounted callout re-reads storage when one of them is dismissed, so
// dismissing one hides the others without waiting for a page load.
const subscribers = new Set<() => void>();

// for testing purposes
export const resetGroupOrderingCalloutPageLoadView = () => {
  viewsAtPageLoad = null;
};

type UseGroupOrderingFeatureCalloutOptions = {
  recordView?: boolean;
};

const useGroupOrderingFeatureCallout = ({
  recordView = true,
}: UseGroupOrderingFeatureCalloutOptions = {}) => {
  const [shouldShowCallout, setShouldShowCallout] = useState(false);

  // localStorage does not exist server-side hence the useEffect
  useEffect(() => {
    const sync = () => {
      if (isDismissed()) {
        setShouldShowCallout(false);

        return;
      }

      const views = recordView ? recordPageLoadView() : (viewsAtPageLoad ?? readViews());

      setShouldShowCallout(views < GROUP_ORDERING_CALLOUT_MAX_VIEWS);
    };

    sync();
    subscribers.add(sync);

    return () => {
      subscribers.delete(sync);
    };
  }, [recordView]);

  // Retires every group ordering callout — now and on later page loads — once
  // the user has acted on the feature they advertise. Wire this into the click
  // handler of anything that promotes group ordering.
  const dismissCallout = useCallback(() => {
    safeLocalStorage.setItem(GROUP_ORDERING_CALLOUT_DISMISSED, 'true');
    subscribers.forEach(subscriber => subscriber());
  }, []);

  return {dismissCallout, shouldShowCallout};
};

export default useGroupOrderingFeatureCallout;
